/**
 * Researcher Management Component (Admin)
 *
 * Admin interface for managing researchers, budgets, and sandbox persona promotion.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Tooltip,
  Avatar,
  FormControlLabel,
  Checkbox,
  Autocomplete
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Publish as PromoteIcon,
  Science as SimulationIcon,
  Person as PersonIcon,
  Psychology as TherapistIcon,
  Settings as SettingsIcon,
  Loop as RLLoopIcon
} from '@mui/icons-material';
import * as researcherApi from '../../services/researcherApi';
import { getAllUsers } from '../../services/api';

const ResearcherManagement = ({ token }) => {
  const [researchers, setResearchers] = useState([]);
  const [stats, setStats] = useState(null);
  const [sandboxPersonas, setSandboxPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // Dialogs
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const [selectedResearcher, setSelectedResearcher] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);

  // Form states
  const [grantForm, setGrantForm] = useState({
    userId: '',
    institution: '',
    researchProject: '',
    dailyLimit: 25,
    monthlyLimit: 100
  });
  const [budgetForm, setBudgetForm] = useState({ dailyLimit: 25, monthlyLimit: 100 });
  const [refreshForm, setRefreshForm] = useState({
    resetDaily: true,
    resetMonthly: false,
    newDailyLimit: null,
    newMonthlyLimit: null
  });
  const [promoteForm, setPromoteForm] = useState({ newKey: '', newName: '' });
  const [editForm, setEditForm] = useState({
    institution: '',
    researchProject: '',
    canAccessRLLoop: false,
    canCreateSandboxPersonas: true,
    maxSandboxPersonas: 50
  });
  const [saving, setSaving] = useState(false);

  // Users list for grant dialog
  const [allUsers, setAllUsers] = useState([]);
  const [eligibleUsers, setEligibleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [researchersData, statsData, personasData] = await Promise.all([
        researcherApi.listResearchers(),
        researcherApi.getResearcherStats(),
        researcherApi.listAllSandboxPersonas({ onlyUnpromoted: 'true' })
      ]);
      setResearchers(researchersData);
      setStats(statsData);
      setSandboxPersonas(personasData);
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadEligibleUsers = async () => {
    try {
      setLoadingUsers(true);
      const users = await getAllUsers(token);
      // Filter out users who already have the researcher role
      const researcherIds = new Set(researchers.map(r => r._id));
      const eligible = users.filter(user => {
        const roles = Array.isArray(user.role) ? user.role : [user.role];
        const isAlreadyResearcher = roles.includes('researcher') || researcherIds.has(user._id);
        return !isAlreadyResearcher;
      });
      setAllUsers(users);
      setEligibleUsers(eligible);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users list');
    } finally {
      setLoadingUsers(false);
    }
  };

  const openGrantDialog = () => {
    setGrantDialogOpen(true);
    loadEligibleUsers();
  };

  const handleGrantRole = async () => {
    if (!selectedUser) {
      setError('Please select a user');
      return;
    }
    try {
      setSaving(true);
      await researcherApi.grantResearcherRole(selectedUser._id, {
        institution: grantForm.institution,
        researchProject: grantForm.researchProject,
        dailyLimit: grantForm.dailyLimit,
        monthlyLimit: grantForm.monthlyLimit
      });
      setGrantDialogOpen(false);
      setGrantForm({ userId: '', institution: '', researchProject: '', dailyLimit: 25, monthlyLimit: 100 });
      setSelectedUser(null);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to grant role');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeRole = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke researcher access?')) return;
    try {
      await researcherApi.revokeResearcherRole(userId);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to revoke role');
    }
  };

  const handleUpdateBudget = async () => {
    try {
      setSaving(true);
      await researcherApi.updateResearcherBudget(selectedResearcher._id, budgetForm);
      setBudgetDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to update budget');
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshBudget = async () => {
    try {
      setSaving(true);
      await researcherApi.refreshResearcherBudget(selectedResearcher._id, refreshForm);
      setRefreshDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to refresh budget');
    } finally {
      setSaving(false);
    }
  };

  const handlePromotePersona = async () => {
    try {
      setSaving(true);
      await researcherApi.promoteSandboxToLive(selectedPersona._id, {
        newKey: promoteForm.newKey || undefined,
        newName: promoteForm.newName || undefined
      });
      setPromoteDialogOpen(false);
      setPromoteForm({ newKey: '', newName: '' });
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to promote persona');
    } finally {
      setSaving(false);
    }
  };

  const openBudgetDialog = (researcher) => {
    setSelectedResearcher(researcher);
    setBudgetForm({
      dailyLimit: researcher.researcherInfo?.budget?.dailyLimit || 25,
      monthlyLimit: researcher.researcherInfo?.budget?.monthlyLimit || 100
    });
    setBudgetDialogOpen(true);
  };

  const openRefreshDialog = (researcher) => {
    setSelectedResearcher(researcher);
    setRefreshForm({ resetDaily: true, resetMonthly: false, newDailyLimit: null, newMonthlyLimit: null });
    setRefreshDialogOpen(true);
  };

  const openPromoteDialog = (persona) => {
    setSelectedPersona(persona);
    setPromoteForm({
      newKey: persona.key.replace(/^sandbox_/, '').replace(/_\d+$/, ''),
      newName: persona.name.replace(/^\[Sandbox\] /, '')
    });
    setPromoteDialogOpen(true);
  };

  const openEditDialog = (researcher) => {
    setSelectedResearcher(researcher);
    setEditForm({
      institution: researcher.researcherInfo?.institution || '',
      researchProject: researcher.researcherInfo?.researchProject || '',
      canAccessRLLoop: researcher.researcherInfo?.canAccessRLLoop || false,
      canCreateSandboxPersonas: researcher.researcherInfo?.canCreateSandboxPersonas !== false,
      maxSandboxPersonas: researcher.researcherInfo?.maxSandboxPersonas || 50
    });
    setEditDialogOpen(true);
  };

  const handleUpdateResearcher = async () => {
    try {
      setSaving(true);
      await researcherApi.updateResearcherSettings(selectedResearcher._id, editForm);
      setEditDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.error || 'Failed to update researcher settings');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => `$${(value || 0).toFixed(2)}`;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Researchers</Typography>
              <Typography variant="h4">{stats?.totalResearchers || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Simulations</Typography>
              <Typography variant="h4">{stats?.totalSimulations || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>This Month</Typography>
              <Typography variant="h4">{stats?.monthlySimulations || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Sandbox Personas</Typography>
              <Typography variant="h4">{stats?.totalSandboxPersonas || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Promoted</Typography>
              <Typography variant="h4">{stats?.promotedPersonas || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box mb={3}>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={openGrantDialog}
        >
          Grant Researcher Role
        </Button>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Researchers (${researchers.length})`} />
        <Tab label={`Pending Promotion (${sandboxPersonas.length})`} />
      </Tabs>

      {/* Researchers Table */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Researcher</TableCell>
                <TableCell>Institution</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Simulations</TableCell>
                <TableCell>Budget Status</TableCell>
                <TableCell>Last Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {researchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="textSecondary">No researchers found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                researchers.map((researcher) => {
                  const budget = researcher.researcherInfo?.budget || {};
                  const dailyPercent = budget.dailyLimit ? (budget.currentDayUsage / budget.dailyLimit) * 100 : 0;
                  const monthlyPercent = budget.monthlyLimit ? (budget.currentMonthUsage / budget.monthlyLimit) * 100 : 0;

                  return (
                    <TableRow key={researcher._id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{researcher.name}</Typography>
                          <Typography variant="caption" color="textSecondary">{researcher.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {researcher.researcherInfo?.institution || '-'}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {researcher.researcherInfo?.canAccessRLLoop && (
                            <Tooltip title="RL Loop Access Granted">
                              <Chip
                                icon={<RLLoopIcon />}
                                label="RL"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {!researcher.researcherInfo?.canAccessRLLoop && (
                            <Typography variant="caption" color="textSecondary">-</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{researcher.simulationCount || 0}</TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 150 }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Daily</Typography>
                            <Typography variant="caption">
                              {formatCurrency(budget.currentDayUsage)} / {formatCurrency(budget.dailyLimit)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(dailyPercent, 100)}
                            color={dailyPercent >= 90 ? 'error' : dailyPercent >= 70 ? 'warning' : 'primary'}
                            sx={{ mb: 1, height: 6 }}
                          />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Monthly</Typography>
                            <Typography variant="caption">
                              {formatCurrency(budget.currentMonthUsage)} / {formatCurrency(budget.monthlyLimit)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(monthlyPercent, 100)}
                            color={monthlyPercent >= 90 ? 'error' : monthlyPercent >= 70 ? 'warning' : 'primary'}
                            sx={{ height: 6 }}
                          />
                          {budget.budgetExhausted && (
                            <Chip label="Exhausted" color="error" size="small" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {researcher.lastActive
                          ? new Date(researcher.lastActive).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Settings">
                          <IconButton size="small" onClick={() => openEditDialog(researcher)}>
                            <SettingsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Budget">
                          <IconButton size="small" onClick={() => openBudgetDialog(researcher)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Refresh Budget">
                          <IconButton size="small" onClick={() => openRefreshDialog(researcher)}>
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revoke Role">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeRole(researcher._id)}
                          >
                            <PersonRemoveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Sandbox Personas for Promotion */}
      {tabValue === 1 && (
        <Grid container spacing={2}>
          {sandboxPersonas.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">No sandbox personas pending promotion</Alert>
            </Grid>
          ) : (
            sandboxPersonas.map((persona) => (
              <Grid item xs={12} md={6} lg={4} key={persona._id}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Avatar sx={{ bgcolor: persona.personaType === 'client' ? 'info.light' : 'success.light' }}>
                        {persona.personaType === 'client' ? <PersonIcon /> : <TherapistIcon />}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight="medium">{persona.name}</Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Key: {persona.key}
                        </Typography>
                        <Chip label={persona.personaType} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                        {persona.createdBy && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            Created by: {persona.createdBy.name || persona.createdBy.email}
                          </Typography>
                        )}
                        {persona.description && (
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {persona.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PromoteIcon />}
                        onClick={() => openPromoteDialog(persona)}
                      >
                        Promote to Live
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Grant Role Dialog */}
      <Dialog open={grantDialogOpen} onClose={() => { setGrantDialogOpen(false); setSelectedUser(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Researcher Role</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={eligibleUsers}
            loading={loadingUsers}
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            getOptionLabel={(option) => `${option.name || 'No name'} (${option.email})`}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option) => {
              const { key, ...restProps } = props;
              const roles = Array.isArray(option.role) ? option.role : [option.role];
              return (
                <Box component="li" key={key} {...restProps}>
                  <Box>
                    <Typography variant="body1">{option.name || 'No name'}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.email} • Roles: {roles.join(', ')}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select User"
                margin="normal"
                placeholder="Search by name or email..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                helperText={`${eligibleUsers.length} users available (excluding existing researchers)`}
              />
            )}
          />
          <TextField
            fullWidth
            label="Institution"
            margin="normal"
            value={grantForm.institution}
            onChange={(e) => setGrantForm({ ...grantForm, institution: e.target.value })}
          />
          <TextField
            fullWidth
            label="Research Project"
            margin="normal"
            value={grantForm.researchProject}
            onChange={(e) => setGrantForm({ ...grantForm, researchProject: e.target.value })}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Daily Budget Limit ($)"
                margin="normal"
                type="number"
                value={grantForm.dailyLimit}
                onChange={(e) => setGrantForm({ ...grantForm, dailyLimit: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Monthly Budget Limit ($)"
                margin="normal"
                type="number"
                value={grantForm.monthlyLimit}
                onChange={(e) => setGrantForm({ ...grantForm, monthlyLimit: parseFloat(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setGrantDialogOpen(false); setSelectedUser(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleGrantRole} disabled={saving || !selectedUser}>
            {saving ? <CircularProgress size={20} /> : 'Grant Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Budget Dialog */}
      <Dialog open={budgetDialogOpen} onClose={() => setBudgetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Budget Limits</DialogTitle>
        <DialogContent dividers>
          {selectedResearcher && (
            <Typography variant="body2" gutterBottom>
              Researcher: <strong>{selectedResearcher.name}</strong>
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Daily Limit ($)"
                margin="normal"
                type="number"
                value={budgetForm.dailyLimit}
                onChange={(e) => setBudgetForm({ ...budgetForm, dailyLimit: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Monthly Limit ($)"
                margin="normal"
                type="number"
                value={budgetForm.monthlyLimit}
                onChange={(e) => setBudgetForm({ ...budgetForm, monthlyLimit: parseFloat(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateBudget} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Budget Dialog */}
      <Dialog open={refreshDialogOpen} onClose={() => setRefreshDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Refresh Budget</DialogTitle>
        <DialogContent dividers>
          {selectedResearcher && (
            <Typography variant="body2" gutterBottom>
              Researcher: <strong>{selectedResearcher.name}</strong>
            </Typography>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={refreshForm.resetDaily}
                onChange={(e) => setRefreshForm({ ...refreshForm, resetDaily: e.target.checked })}
              />
            }
            label="Reset Daily Usage"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={refreshForm.resetMonthly}
                onChange={(e) => setRefreshForm({ ...refreshForm, resetMonthly: e.target.checked })}
              />
            }
            label="Reset Monthly Usage"
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Optionally set new limits:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="New Daily Limit ($)"
                margin="normal"
                type="number"
                value={refreshForm.newDailyLimit || ''}
                onChange={(e) => setRefreshForm({
                  ...refreshForm,
                  newDailyLimit: e.target.value ? parseFloat(e.target.value) : null
                })}
                placeholder="Keep current"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="New Monthly Limit ($)"
                margin="normal"
                type="number"
                value={refreshForm.newMonthlyLimit || ''}
                onChange={(e) => setRefreshForm({
                  ...refreshForm,
                  newMonthlyLimit: e.target.value ? parseFloat(e.target.value) : null
                })}
                placeholder="Keep current"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefreshDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRefreshBudget} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Refresh'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote Persona Dialog */}
      <Dialog open={promoteDialogOpen} onClose={() => setPromoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Promote to Live System</DialogTitle>
        <DialogContent dividers>
          {selectedPersona && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will create a new live {selectedPersona.personaType} persona. This action cannot be undone.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Live Persona Key"
            margin="normal"
            value={promoteForm.newKey}
            onChange={(e) => setPromoteForm({ ...promoteForm, newKey: e.target.value })}
            helperText="The key for the new live persona"
          />
          <TextField
            fullWidth
            label="Live Persona Name"
            margin="normal"
            value={promoteForm.newName}
            onChange={(e) => setPromoteForm({ ...promoteForm, newName: e.target.value })}
            helperText="The display name for the live persona"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handlePromotePersona} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Promote to Live'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Researcher Settings Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Researcher Settings</DialogTitle>
        <DialogContent dividers>
          {selectedResearcher && (
            <Typography variant="body2" gutterBottom>
              Researcher: <strong>{selectedResearcher.name}</strong> ({selectedResearcher.email})
            </Typography>
          )}

          <TextField
            fullWidth
            label="Institution"
            margin="normal"
            value={editForm.institution}
            onChange={(e) => setEditForm({ ...editForm, institution: e.target.value })}
          />

          <TextField
            fullWidth
            label="Research Project"
            margin="normal"
            value={editForm.researchProject}
            onChange={(e) => setEditForm({ ...editForm, researchProject: e.target.value })}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Feature Access</Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={editForm.canAccessRLLoop}
                onChange={(e) => setEditForm({ ...editForm, canAccessRLLoop: e.target.checked })}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <RLLoopIcon fontSize="small" color={editForm.canAccessRLLoop ? 'primary' : 'disabled'} />
                <span>RL Loop Access</span>
              </Box>
            }
          />
          <Typography variant="caption" color="textSecondary" display="block" sx={{ ml: 4, mb: 2 }}>
            Enables access to the RL Loop feature for automated persona improvement
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={editForm.canCreateSandboxPersonas}
                onChange={(e) => setEditForm({ ...editForm, canCreateSandboxPersonas: e.target.checked })}
              />
            }
            label="Can Create Sandbox Personas"
          />

          <TextField
            fullWidth
            label="Max Sandbox Personas"
            margin="normal"
            type="number"
            value={editForm.maxSandboxPersonas}
            onChange={(e) => setEditForm({ ...editForm, maxSandboxPersonas: parseInt(e.target.value) || 50 })}
            inputProps={{ min: 1, max: 500 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateResearcher} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResearcherManagement;
